// ProgresoFidelizacion.tsx
//
// Avance de las dos operaciones del lote: la consulta de datos de los clientes
// y el envío de la plantilla HSM. Cada fila combina el proceso en vivo (si es
// esa fase la que corre) con los contadores del lote, que quedan guardados, para
// que el estado siga siendo visible aunque se recargue la pantalla o el
// proceso ya haya terminado.

import { Alert, Box, Chip, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import type {
  FidelizacionLote,
  FidelizacionProceso,
} from "../fidelizacion-campana.interface";

type Fase = "CONSULTA" | "ENVIO";

interface Props {
  lote: FidelizacionLote | null;
  proceso: FidelizacionProceso | null;
}

///* lo que el lote ya tiene resuelto, para cuando no hay proceso en vivo
interface Resumen {
  hechos: number;
  total: number;
  ok: number;
  sinDato: number;
  fallidos: number;
  detalle: string;
}

const resumenConsulta = (lote: FidelizacionLote): Resumen => {
  const total = lote.total_filas ?? 0;
  const sinTelefono = lote.sin_telefono ?? 0;
  const noEncontrado = lote.no_encontrado ?? 0;
  const contactables = lote.contactables ?? 0;
  const errores = lote.errores_consulta ?? 0;
  return {
    hechos: total - (lote.por_consultar ?? 0),
    total,
    ok: contactables + sinTelefono,
    sinDato: noEncontrado,
    fallidos: errores,
    ///* se habla en contactables porque es lo que decide si el mensaje sale o
    ///* si hay que ir a corregir el dato en el sistema de origen
    detalle: `${contactables} contactables · ${sinTelefono + noEncontrado + errores} no contactables${
      sinTelefono ? ` (${sinTelefono} sin teléfono)` : ""
    }${noEncontrado ? ` (${noEncontrado} no registrados)` : ""}${errores ? ` (${errores} con error)` : ""}`,
  };
};

const resumenEnvio = (lote: FidelizacionLote): Resumen => {
  const contactables = lote.contactables ?? 0;
  const enviados = lote.enviados ?? 0;
  const errores = lote.errores_envio ?? 0;
  return {
    hechos: enviados + errores,
    total: contactables,
    ok: enviados,
    sinDato: 0,
    fallidos: errores,
    detalle: `${enviados} enviados${errores ? ` · ${errores} rechazados` : ""}${
      contactables === 0 ? " · todavía no hay contactos contactables" : ""
    }`,
  };
};

const pct = (hechos: number, total: number) => (total > 0 ? Math.round((hechos / total) * 100) : 0);

///* avance de cada fase segun los contadores del lote. Se exportan porque la
///* lista de campañas los muestra tambien y no debe repetir el calculo
export const pctConsulta = (lote: FidelizacionLote) =>
  pct((lote.total_filas ?? 0) - (lote.por_consultar ?? 0), lote.total_filas ?? 0);

export const pctEnvio = (lote: FidelizacionLote) =>
  pct((lote.enviados ?? 0) + (lote.errores_envio ?? 0), lote.contactables ?? 0);

///* una fila por fase: la de arriba es la consulta, la de abajo el envío
const FilaFase = ({
  titulo,
  enCurso,
  errores,
  resumen,
}: {
  titulo: string;
  enCurso: boolean;
  errores?: string[];
  resumen: Resumen;
}) => {
  const terminado = !enCurso && resumen.hechos > 0;
  const sinEmpezar = !enCurso && resumen.hechos === 0;
  const color = enCurso ? "info" : resumen.fallidos > 0 ? "warning" : terminado ? "success" : "default";

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{titulo}</Typography>
        <Chip
          size="small"
          color={color}
          label={
            enCurso
              ? `en curso ${pct(resumen.hechos, resumen.total)}%`
              : sinEmpezar
                ? "sin empezar"
                : `finalizada ${pct(resumen.hechos, resumen.total)}%`
          }
        />
      </Stack>
      <LinearProgress
        variant="determinate"
        value={enCurso ? pct(resumen.hechos, resumen.total) : pct(resumen.hechos, resumen.total)}
        color={resumen.fallidos > 0 && !enCurso ? "warning" : enCurso ? "info" : "success"}
        sx={{ height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary">
        {`${resumen.hechos}/${resumen.total} procesadas`}
        {resumen.ok > 0 && ` · ${resumen.ok} con resultado`}
        {resumen.sinDato > 0 && ` · ${resumen.sinDato} sin dato`}
        {resumen.fallidos > 0 && ` · ${resumen.fallidos} con error`}
        {resumen.detalle ? ` — ${resumen.detalle}` : ""}
      </Typography>
      {!!errores?.length && (
        <Alert severity="warning" sx={{ mt: 0.5 }}>
          {errores.slice(-4).map((error, index) => (
            <Typography key={index} variant="caption" display="block">
              {error}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export const ProgresoFidelizacion = ({ lote, proceso }: Props) => {
  if (!lote) return null;

  const enCurso = proceso?.estado === "CORRIENDO";
  const tipoEnCurso = enCurso ? (proceso?.tipo as Fase | undefined) : undefined;

  ///* si hay proceso en vivo mandan sus contadores; si no, los del lote
  const consulta: Resumen =
    tipoEnCurso === "CONSULTA" && proceso
      ? {
          hechos: proceso.procesados,
          total: proceso.total,
          ok: proceso.exitos,
          sinDato: 0,
          fallidos: proceso.fallidos,
          detalle: proceso.fase,
        }
      : resumenConsulta(lote);

  const envio: Resumen =
    tipoEnCurso === "ENVIO" && proceso
      ? {
          hechos: proceso.procesados,
          total: proceso.total,
          ok: proceso.exitos,
          sinDato: 0,
          fallidos: proceso.fallidos,
          detalle: proceso.fase,
        }
      : resumenEnvio(lote);

  const erroresConsulta = tipoEnCurso === "CONSULTA" ? proceso?.errores : undefined;
  const erroresEnvio = tipoEnCurso === "ENVIO" ? proceso?.errores : undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <FilaFase titulo="Consulta de datos de clientes" enCurso={tipoEnCurso === "CONSULTA"} resumen={consulta} errores={erroresConsulta} />
        <FilaFase titulo="Envío de campaña" enCurso={tipoEnCurso === "ENVIO"} resumen={envio} errores={erroresEnvio} />
        {enCurso && (
          <Typography variant="caption" color="text.secondary">
            {`Proceso en marcha desde las ${new Date(proceso!.fecha_inicio).toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit" })}.`}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
